import { FormlyExtension, FormlyFieldConfig } from '@ngx-formly/core'
import { TranslateService } from '@ngx-translate/core'
import { merge, Observable, tap } from 'rxjs'

import { FormlyFieldProps } from './components/formly/types/common.types'
import { formlyConfig } from './formly.config'

export class TranslateExtension implements FormlyExtension {
  public constructor(private readonly translateService: TranslateService) {}

  public prePopulate(field: FormlyFieldConfig): void {
    field.hooks = field.hooks || {}

    const translateObservables = [
      ...this.getTranslateAttributeObservables(field),
      ...this.getTranslateValidatorObservables(field),
      ...this.getGlobalValidationMessages(field),
    ]

    // save existing onInit hook
    if (field.hooks.onInit) {
      translateObservables.push(field.hooks.onInit(field) as Observable<string>)
    }

    field.hooks.onInit = (): Observable<unknown> =>
      merge(...translateObservables)
  }

  private getGlobalValidationMessages(
    field: FormlyFieldConfig
  ): Observable<string>[] {
    return (formlyConfig.validationMessages ?? [])
      .filter((m) => typeof m.message === 'string')
      .filter(
        (m) => !Object.keys(field.validation?.messages ?? {}).includes(m.name)
      )
      .map(({ message, name }) =>
        this.translateService
          .stream(message as string)
          .pipe(
            tap((value: string) => (field.validation!.messages![name] = value))
          )
      )
  }

  private getTranslateValidatorObservables(
    field: FormlyFieldConfig
  ): Observable<string>[] {
    return Object.entries(field.validation?.messages ?? {})
      .filter(([, value]) => typeof value === 'string')
      .map(([validatorKey, value]) =>
        this.translateService
          .stream(value as string)
          .pipe(
            tap(
              (value: string) =>
                (field.validation!.messages![validatorKey] = value)
            )
          )
      )
  }

  private getTranslateAttributeObservables(
    field: FormlyFieldConfig
  ): Observable<string>[] {
    return (
      [
        'label',
        'placeholder',
        'description',
      ] satisfies (keyof FormlyFieldProps)[]
    )
      .filter((attribute) => field.props && field.props[attribute])
      .map((attribute) =>
        this.translateService
          .stream(field.props![attribute] as string)
          .pipe(
            tap((value: string) => (field.props![attribute as string] = value))
          )
      )
  }
}
